// offline-channel.ts — cross-tab coordination for backlit.offline: a
// BroadcastChannel wrapper carrying a small, strictly-validated message set,
// the drain lock (only one tab replays the outbox at a time), and this tab's id.
//
// Messages, every one stamped {v:1, from}:
//   purge            — another tab purged the mirror (all, or one user's data)
//   identity         — another tab saw the signed-in identity change
//   outbox           — a poke: the outbox changed, consider draining
//   change           — a relayed local storage.change (same uid only)
//   rebase-request   — "re-run your updateJSON closures for this op on this
//                       remote value" (the conflict pipeline, S7b)
//   rebase-response  — the rebased value, as encodeJSON text (or null)
//   pause            — the server asked this glow's drain to wait until
//                       `until` (epoch ms): a 429 or a long pause (S3b)
//   reachable        — another tab's reachability probe got an answer from
//                       the glow at `at` (epoch ms): the connection is back
// A tab drops its own messages (from === its tab id) and anything that fails
// validation, so a stale or foreign sender can't inject a malformed message.
//
// The drain lock prefers Web Locks (`navigator.locks`, ifAvailable — never
// queues behind another tab), falls back to a caller-supplied lease hook (the
// outbox's claim-with-TTL, SYNC-2), and otherwise FAILS CLOSED: no lock, no
// drain.
//
// MUST-MATCH: CHANNEL_NAME / DRAIN_LOCK_NAME are shared by every SDK build that
// may run in another tab of the same glow.

export const CHANNEL_NAME = "backlit-offline";
export const DRAIN_LOCK_NAME = "backlit-offline-drain";

// TAB_ID identifies this page (for dropping its own channel messages and
// stamping outbox ops with their origin tab).
export const TAB_ID: string = makeTabId();

// randomHex is 16 random bytes as 32 lowercase hex characters (outbox op ids;
// throws where there is no crypto.getRandomValues).
export function randomHex(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (x) => x.toString(16).padStart(2, "0")).join("");
}

function makeTabId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  try {
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch {
    /* insecure context: fall through */
  }
  try {
    return randomHex();
  } catch {
    return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  }
}

// ChangeRelay is a storage.change as relayed to other tabs of the same uid.
export type ChangeRelay = {
  store: "data" | "userdata" | "records";
  operation: "set" | "delete";
  key: string;
  crc32c: string;
  owner?: string;
  pending?: true;
};

type Envelope = { v: 1; from: string };

export type PurgeMessage = Envelope & { type: "purge"; reason: string; scope: "all" | "user"; uid?: string };
export type IdentityMessage = Envelope & { type: "identity"; uid: string; gen: number };
export type OutboxMessage = Envelope & { type: "outbox" };
export type ChangeMessage = Envelope & { type: "change"; uid: string; ev: ChangeRelay };
export type RebaseRequestMessage = Envelope & {
  type: "rebase-request";
  rid: string;
  opId: string;
  writes: number;
  remote: { bytes: ArrayBuffer; contentType: string; crc32c: string } | null;
};
export type RebaseResponseMessage = Envelope & {
  type: "rebase-response";
  rid: string;
  value: string | null;
  maxAttempts: number;
};

export type PauseMessage = Envelope & { type: "pause"; until: number };
export type ReachableMessage = Envelope & { type: "reachable"; at: number };

export type ChannelMessage =
  | PurgeMessage
  | IdentityMessage
  | OutboxMessage
  | ChangeMessage
  | RebaseRequestMessage
  | RebaseResponseMessage
  | PauseMessage
  | ReachableMessage;

// OutgoingMessage is a ChannelMessage before post() stamps {v, from}.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
export type OutgoingMessage = DistributiveOmit<ChannelMessage, "v" | "from">;

export interface OfflineChannel {
  post(m: OutgoingMessage): void;
  // on registers a listener; the returned function unregisters it.
  on(fn: (m: ChannelMessage) => void): () => void;
  close(): void;
}

type Rec = Record<string, unknown>;

const isObj = (x: unknown): x is Rec => typeof x === "object" && x !== null && !Array.isArray(x);
const isStr = (x: unknown): x is string => typeof x === "string";
const isCount = (x: unknown, min: number): x is number => Number.isInteger(x) && (x as number) >= min;
const optStr = (x: unknown) => x === undefined || isStr(x);

function parseChange(ev: unknown): ChangeRelay | null {
  if (
    !isObj(ev) ||
    (ev.store !== "data" && ev.store !== "userdata" && ev.store !== "records") ||
    (ev.operation !== "set" && ev.operation !== "delete") ||
    !isStr(ev.key) ||
    !isStr(ev.crc32c) ||
    !optStr(ev.owner) ||
    (ev.pending !== undefined && ev.pending !== true)
  ) {
    return null;
  }
  const out: ChangeRelay = { store: ev.store, operation: ev.operation, key: ev.key, crc32c: ev.crc32c };
  if (isStr(ev.owner)) out.owner = ev.owner;
  if (ev.pending === true) out.pending = true;
  return out;
}

// parseChannelMessage validates a received message and returns a normalized
// copy (unknown fields dropped), or null on any mismatch.
export function parseChannelMessage(raw: unknown): ChannelMessage | null {
  if (!isObj(raw) || raw.v !== 1 || !isStr(raw.from) || raw.from === "") return null;
  const from = raw.from;
  switch (raw.type) {
    case "purge": {
      if (!isStr(raw.reason) || (raw.scope !== "all" && raw.scope !== "user") || !optStr(raw.uid)) return null;
      const m: PurgeMessage = { v: 1, from, type: "purge", reason: raw.reason, scope: raw.scope };
      if (isStr(raw.uid)) m.uid = raw.uid;
      return m;
    }
    case "identity":
      if (!isStr(raw.uid) || !isCount(raw.gen, 0)) return null;
      return { v: 1, from, type: "identity", uid: raw.uid, gen: raw.gen };
    case "outbox":
      return { v: 1, from, type: "outbox" };
    case "change": {
      const ev = parseChange(raw.ev);
      if (!isStr(raw.uid) || !ev) return null;
      return { v: 1, from, type: "change", uid: raw.uid, ev };
    }
    case "rebase-request": {
      const r = raw.remote;
      let remote: RebaseRequestMessage["remote"] = null;
      if (r !== null) {
        if (!isObj(r) || !(r.bytes instanceof ArrayBuffer) || !isStr(r.contentType) || !isStr(r.crc32c)) return null;
        remote = { bytes: r.bytes, contentType: r.contentType, crc32c: r.crc32c };
      }
      if (!isStr(raw.rid) || !isStr(raw.opId) || !isCount(raw.writes, 1)) return null;
      return { v: 1, from, type: "rebase-request", rid: raw.rid, opId: raw.opId, writes: raw.writes, remote };
    }
    case "rebase-response":
      if (!isStr(raw.rid) || (raw.value !== null && !isStr(raw.value)) || !isCount(raw.maxAttempts, 0)) return null;
      return { v: 1, from, type: "rebase-response", rid: raw.rid, value: raw.value, maxAttempts: raw.maxAttempts };
    case "pause":
      if (typeof raw.until !== "number" || !Number.isFinite(raw.until)) return null;
      return { v: 1, from, type: "pause", until: raw.until };
    case "reachable":
      if (typeof raw.at !== "number" || !Number.isFinite(raw.at)) return null;
      return { v: 1, from, type: "reachable", at: raw.at };
    default:
      return null;
  }
}

// openChannel opens the offline BroadcastChannel, or returns null where there
// is none (ctor defaults to globalThis.BroadcastChannel; pass null to force
// "none"). post() stamps {v:1, from: tabId} and never throws; a closed channel
// ignores post(). Incoming messages from this tab, or that fail validation,
// are dropped; a throwing listener doesn't stop the others.
export function openChannel(
  o: { ctor?: (new (n: string) => BroadcastChannel) | null; tabId?: string } = {},
): OfflineChannel | null {
  const Ctor = o.ctor === undefined ? (globalThis as { BroadcastChannel?: new (n: string) => BroadcastChannel }).BroadcastChannel : o.ctor;
  if (typeof Ctor !== "function") return null;
  const tabId = o.tabId ?? TAB_ID;
  let bc: BroadcastChannel;
  try {
    bc = new Ctor(CHANNEL_NAME);
  } catch {
    return null;
  }
  const listeners = new Set<(m: ChannelMessage) => void>();
  let closed = false;
  bc.onmessage = (ev: MessageEvent) => {
    const m = parseChannelMessage(ev.data);
    if (!m || m.from === tabId) return;
    for (const fn of [...listeners]) {
      try {
        fn(m);
      } catch {
        /* one listener's failure never reaches the others */
      }
    }
  };
  return {
    post(m) {
      if (closed) return;
      try {
        bc.postMessage({ ...m, v: 1, from: tabId });
      } catch {
        /* e.g. DataCloneError: best-effort */
      }
    },
    on(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    close() {
      if (closed) return;
      closed = true;
      listeners.clear();
      try {
        bc.close();
      } catch {
        /* already closed */
      }
    },
  };
}

// LeaseHook is the fallback exclusion where Web Locks are missing: an
// outbox-backed lease claimed in one transaction.
export interface LeaseHook {
  tryAcquire(): Promise<boolean>;
  release(): Promise<void>;
}

export type DrainLockResult<T> = { acquired: true; value: T } | { acquired: false };

function webLocks(): LockManager | null {
  try {
    return (globalThis as { navigator?: { locks?: LockManager } }).navigator?.locks ?? null;
  } catch {
    return null;
  }
}

// withDrainLock runs fn while holding the cross-tab drain lock, or reports
// {acquired:false} without running it when another tab holds the lock, or when
// no exclusion mechanism exists (fail closed). locks defaults to
// navigator.locks; pass null to force the lease path. An error thrown by fn
// propagates after the lock / lease is released.
export async function withDrainLock<T>(
  fn: () => T | Promise<T>,
  o: { locks?: LockManager | null; lease?: LeaseHook | null } = {},
): Promise<DrainLockResult<T>> {
  const locks = o.locks === undefined ? webLocks() : o.locks;
  if (locks) {
    let ran = false;
    try {
      return await locks.request(DRAIN_LOCK_NAME, { ifAvailable: true }, async (lock) => {
        if (lock === null) return { acquired: false } as const;
        ran = true;
        return { acquired: true, value: await fn() } as const;
      });
    } catch (err) {
      // fn's own error propagates; a failure to even request the lock (e.g. a
      // SecurityError in an opaque origin) falls through to the lease.
      if (ran) throw err;
    }
  }
  const lease = o.lease ?? null;
  if (!lease) return { acquired: false };
  let got = false;
  try {
    got = await lease.tryAcquire();
  } catch {
    got = false;
  }
  if (!got) return { acquired: false };
  try {
    return { acquired: true, value: await fn() };
  } finally {
    try {
      await lease.release();
    } catch {
      /* the lease TTL reclaims it */
    }
  }
}
